// Per-dialect synthesizer for UNIQUE constraint add/drop ops (v2 Slice B3).
//
// PRD: Ralph/DdlStructuralEdit2/PRD.md § Slice B3.
//
// Each engine emits the dialect-correct ADD/DROP CONSTRAINT clause:
//   - mssql:  `ADD [CONSTRAINT name] UNIQUE [CLUSTERED|NONCLUSTERED] (cols)`
//             / `DROP CONSTRAINT name`. Filtered unique indexes are owned
//             by slice C1 (`add-index` with `unique: true` + filter).
//   - pgsql:  `ADD [CONSTRAINT name] UNIQUE (cols) [INCLUDE (cols)]
//             [NULLS {DISTINCT|NOT DISTINCT}] [DEFERRABLE INITIALLY …]`
//             / `DROP CONSTRAINT name [CASCADE]`.
//   - mysql:  `ADD [CONSTRAINT name] UNIQUE (cols)`
//             / `DROP INDEX name`. mysql stores UNIQUE constraints as
//             unique indexes; `DROP INDEX <name>` is the universally
//             supported drop idiom (DROP CONSTRAINT is 8.0.19+).
//   - oracle: `ADD CONSTRAINT name UNIQUE (cols) [USING INDEX TABLESPACE ts]
//             [DEFERRABLE INITIALLY {DEFERRED|IMMEDIATE}]`
//             / `DROP CONSTRAINT name [CASCADE]`.
//   - sqlite: not supported on ALTER → throws (DSE2-6). UNIQUE in CREATE
//             TABLE only; there is no automated recreate helper for this op
//             (the opt-in `sqliteRecreate` primitive only rewrites columns) —
//             the table must be rebuilt by hand.

import type { QualifiedName, SqlEngine } from '../../../sql-grammar/model';
import type { UniqueConstraintDef } from '../../types';
import { DdlEditError } from '../../types';

import { maybeQuoteIdent } from './dialect';
import { type ExistingNameOrigin, nameExistingColumn } from './dialect.quoting';
import type { ColumnListOrigins } from './existingNameEvidence';

export interface UniqueConstraintSynthOpts {
  /** The DSE6 STYLE flag — the host relation's own quoting, mirrored onto the
   *  column tokens. A style may ADD delimiters; it may never decide WHICH
   *  columns are named. See `columnNameOrigin`. */
  readonly quoteName: boolean;
  /** WHERE the spelling at each POSITION of `uq.columns` came from. A UNIQUE
   *  constraint names columns the catalog ALREADY HOLDS — exactly like a
   *  rename's old name, a drop's target, an ALTER COLUMN's subject and the
   *  PRIMARY KEY / FOREIGN KEY / index lists — so every element sits on the
   *  same three-way contract (`dialect.quoting.ts`'s
   *  {@link ExistingNameOrigin}) and `quoteName` alone cannot answer it.
   *  Asked PER ELEMENT because one list may legitimately hold a
   *  source-delimited `"Id"` next to a bare `target_col`, and a single answer
   *  for the whole list corrupts one of them. Defaults to `'bare-reference'`,
   *  the contract a caller holding only a name is under. */
  readonly columnNameOrigin?: ColumnListOrigins;
  /** The same question for `uq.includeColumns`, asked SEPARATELY. INCLUDE is
   *  a second list on one edit, not a continuation of the first: an element is
   *  identified by its position in the list being emitted, so a shared lookup
   *  serves one list's answer to the other and the key names a different
   *  column. The server does not stop it — PostgreSQL accepts an INCLUDE that
   *  overlaps its own key — so the separation has to hold here. Defaults to
   *  `'bare-reference'` independently of `columnNameOrigin`. */
  readonly includeColumnNameOrigin?: ColumnListOrigins;
}

/** The default origin resolver: every element is a reference the caller wrote
 *  bare, which is what a free-text column list is until the source statement
 *  says otherwise. */
const BARE_REFERENCE = (): ExistingNameOrigin => 'bare-reference';

function tableText(engine: SqlEngine, table: QualifiedName): string {
  if (table.raw && table.raw.length > 0) return table.raw;
  const name = maybeQuoteIdent(engine, table.name, table.quoted);
  if (table.schema) {
    const schema = maybeQuoteIdent(engine, table.schema, table.quoted);
    return `${schema}.${name}`;
  }
  return name;
}

/** Name each column the constraint lists. `styleDelimits` may only ADD
 *  delimiters; `nameExistingColumn` picks the TEXT that goes inside them per
 *  element, because adding delimiters to a bare reference suppresses the very
 *  fold that was resolving it, and folding a spelling the server already
 *  stores names a different column.
 *
 *  The origin is asked for by POSITION: two elements of one list may carry the
 *  same bytes (`Id` and `"Id"` decode alike) and only their positions tell
 *  them apart. */
function quoteCols(
  engine: SqlEngine,
  cols: readonly string[],
  styleDelimits: boolean,
  originAt: ColumnListOrigins,
): string {
  return cols.map((c, i) => {
    const { text, delimit } = nameExistingColumn(c, engine, originAt(i), styleDelimits);
    return maybeQuoteIdent(engine, text, delimit);
  }).join(', ');
}

function rejectField(engine: SqlEngine, field: string): never {
  throw new Error(
    `applyDdlEdit: ${engine} UNIQUE constraint does not accept '${field}'`,
  );
}

function validateUq(engine: SqlEngine, uq: UniqueConstraintDef): void {
  if (uq.columns.length === 0) {
    throw new Error(`applyDdlEdit: UNIQUE constraint requires at least one column`);
  }
  if (engine !== 'mssql' && uq.clustered !== undefined) rejectField(engine, 'clustered');
  if (engine !== 'postgres' && uq.includeColumns !== undefined) {
    rejectField(engine, 'includeColumns');
  }
  if (engine !== 'postgres' && uq.nullsDistinct !== undefined) {
    rejectField(engine, 'nullsDistinct');
  }
  if (engine !== 'oracle' && uq.tablespace !== undefined) rejectField(engine, 'tablespace');
  if (engine !== 'oracle' && engine !== 'postgres' && uq.deferrable !== undefined) {
    rejectField(engine, 'deferrable');
  }
}

export function synthesizeAddUniqueConstraint(
  engine: SqlEngine,
  table: QualifiedName,
  uq: UniqueConstraintDef,
  opts: UniqueConstraintSynthOpts,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … ADD CONSTRAINT `
        + `UNIQUE. Define the unique constraint inline at CREATE TABLE time, `
        + `or rebuild the table manually (CREATE TABLE with the constraint, `
        + `copy the data across, DROP the old table, RENAME the new one) — `
        + `the opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  validateUq(engine, uq);
  const tbl = tableText(engine, table);
  const originOf = opts.columnNameOrigin ?? BARE_REFERENCE;
  const cols = quoteCols(engine, uq.columns, opts.quoteName, originOf);
  const constraintPrefix = uq.name ? `CONSTRAINT ${uq.name} ` : '';

  if (engine === 'mssql') {
    const cluster = uq.clustered === 'clustered'
      ? ' CLUSTERED'
      : uq.clustered === 'nonclustered' ? ' NONCLUSTERED' : '';
    return `ALTER TABLE ${tbl} ADD ${constraintPrefix}UNIQUE${cluster} (${cols})`;
  }

  if (engine === 'postgres') {
    let s = `ALTER TABLE ${tbl} ADD ${constraintPrefix}UNIQUE`;
    if (uq.nullsDistinct !== undefined) {
      s += uq.nullsDistinct ? ' NULLS DISTINCT' : ' NULLS NOT DISTINCT';
    }
    s += ` (${cols})`;
    if (uq.includeColumns && uq.includeColumns.length > 0) {
      const includeOriginOf = opts.includeColumnNameOrigin ?? BARE_REFERENCE;
      s += ` INCLUDE (${quoteCols(engine, uq.includeColumns, opts.quoteName, includeOriginOf)})`;
    }
    if (uq.deferrable !== undefined) {
      s += ` DEFERRABLE INITIALLY ${uq.deferrable === 'deferred' ? 'DEFERRED' : 'IMMEDIATE'}`;
    }
    return s;
  }

  if (engine === 'mysql') {
    return `ALTER TABLE ${tbl} ADD ${constraintPrefix}UNIQUE (${cols})`;
  }

  // oracle
  let s = `ALTER TABLE ${tbl} ADD ${constraintPrefix}UNIQUE (${cols})`;
  if (uq.tablespace) s += ` USING INDEX TABLESPACE ${uq.tablespace}`;
  if (uq.deferrable !== undefined) {
    s += ` DEFERRABLE INITIALLY ${uq.deferrable === 'deferred' ? 'DEFERRED' : 'IMMEDIATE'}`;
  }
  return s;
}

export function synthesizeDropUniqueConstraint(
  engine: SqlEngine,
  table: QualifiedName,
  constraintName: string,
  cascade: boolean | undefined,
): string {
  if (engine === 'sqlite') {
    throw new DdlEditError(
      'engine-does-not-support-op',
      `applyDdlEdit: SQLite does not support ALTER TABLE … DROP UNIQUE. `
        + `Rebuild the table manually (CREATE TABLE without the constraint, `
        + `copy the data across, DROP the old table, RENAME the new one) — `
        + `the opt-in 'sqliteRecreate' path only rewrites columns `
        + `(add/drop/rename/alter), not table-level constraints.`,
    );
  }
  if (!constraintName || constraintName.length === 0) {
    throw new Error(
      `applyDdlEdit: ${engine} DROP UNIQUE constraint requires 'constraintName'`,
    );
  }
  const tbl = tableText(engine, table);

  if (engine === 'mysql') {
    // mysql stores UNIQUE constraints as unique indexes; `DROP INDEX <name>`
    // is the older, universally-supported idiom (DROP CONSTRAINT is 8.0.19+).
    // CASCADE is not accepted in this position on mysql — silently ignored.
    return `ALTER TABLE ${tbl} DROP INDEX ${constraintName}`;
  }

  const cas = (engine === 'postgres' || engine === 'oracle') && cascade ? ' CASCADE' : '';
  return `ALTER TABLE ${tbl} DROP CONSTRAINT ${constraintName}${cas}`;
}
